import React, { useState, useEffect } from "react";
import { Loader2, Sparkles, Zap } from "lucide-react";
import { SectionHeading, StepInstructions } from "@/components/wizard/ui";
import { DEMAND_COLOR, SPORTS } from "@/lib/planModel";
import { SportIcon } from "@/components/wizard/icons";
import FuellingDay from "@/components/wizard/FuellingDay";
import LowEnergyWarning from "@/components/wizard/LowEnergyWarning";

import { DemandInfo } from "@/components/wizard/InfoTip";
import { litresLabel } from "@/lib/units";
import { fmtDuration, fmtDistanceFromMi } from "@/lib/format";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const DEMAND_LABEL = { rest: "Rest", low: "Low", moderate: "Moderate", high: "High", very_high: "Very High" };
const SPORT_LABEL = Object.fromEntries(SPORTS.map((s) => [s.value, s.label]));
const INTENSITY_LABEL = { recovery: "Recovery", endurance: "Endurance", tempo: "Tempo", threshold: "Threshold", vo2: "VO2", race: "Race" };

// TrainingPeaks-style day card: coloured spine on the left, training block on
// top ("what am I doing?"), fuelling target directly beneath ("how do I feed
// it?"). No chart wall — one page tells the athlete today's action.
function DayCard({ d, sessions, fuel, unitSystem, isToday }) {
  const spine = DEMAND_COLOR[d.demand] || "#71717A";
  return (
    <div data-testid={`daily-card-${d.index}`}
      className={`border bg-[#141414] flex overflow-hidden h-full transition-shadow ${isToday ? "border-[#E2E30A]" : "border-white/10"}`}
      style={isToday ? { boxShadow: "0 0 0 1px #E2E30A, 0 0 24px rgba(226,227,10,0.25)" } : undefined}>
      <div className="w-1.5 shrink-0" style={{ background: spine }} aria-hidden />
      <div className="flex-1 p-4">
        {/* Day header — day name + demand chip, no more, no less. */}
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-xl uppercase text-white">{d.day}</span>
            {isToday && <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#E2E30A]">Today</span>}
          </div>
          <span className="inline-flex items-center gap-1 flex-wrap justify-end">
            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-0.5"
              style={{ color: "#F5F1E8", border: `1px solid ${spine}`, background: "transparent" }}>
              {d.isRace ? "Race" : DEMAND_LABEL[d.demand]}
            </span>
            {!d.isRace && (d.carbLoadBumpGkg || 0) > 0 && (d.daysUntilRace || 0) > 0 && (
              <span data-testid={`daily-carbload-${d.index}`}
                className="inline-flex items-center gap-1 border border-[#E2E30A]/60 bg-[#E2E30A]/[0.08] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-[#E2E30A]">
                <Zap className="h-2.5 w-2.5" /> T-{d.daysUntilRace} · +{d.carbLoadBumpGkg}g/kg
              </span>
            )}
            <DemandInfo />
          </span>
        </div>

        {/* TRAINING FIRST — what's happening today? */}
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 mb-2">Training</p>
          {sessions && sessions.length ? (
            <ul className="space-y-1.5" data-testid={`sessions-${d.index}`}>
              {sessions.map((s, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <SportIcon sport={s.sport} className="h-4 w-4 shrink-0 text-[#E2E30A]" />
                  <span className="text-white font-semibold">{SPORT_LABEL[s.sport] || s.sport}</span>
                  <span className="text-zinc-400 font-mono tabular-nums text-xs">{fmtDuration(s.duration)}</span>
                  {s.tpDistanceMi ? <span className="text-zinc-300 font-mono tabular-nums text-xs">· {fmtDistanceFromMi(s.tpDistanceMi, unitSystem)}</span> : null}
                  {s.intensity && s.intensity !== "endurance" && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-400">{INTENSITY_LABEL[s.intensity] || s.intensity}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-zinc-300 italic">Rest day — nothing scheduled.</p>
          )}
          <p className="text-[11px] text-zinc-300 mt-2 tabular-nums">Total {fmtDuration(d.trainingMinutes)} · burns ~{d.trainingKcal} kcal</p>
        </div>

        {/* NUTRITION SECOND — how do I fuel it? */}
        <div className="border-t border-white/10 pt-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 mb-2">Nutrition Target</p>
          <div className="flex items-baseline gap-2 mb-3">
            <p className="font-display text-3xl text-white leading-none tabular-nums">{d.energy}</p>
            <p className="text-xs text-zinc-400">kcal</p>
          </div>
          <div className="grid grid-cols-4 gap-3 text-xs tabular-nums">
            <div><p className="text-[10px] uppercase tracking-wider text-zinc-300">Pro</p><p className="text-white font-semibold mt-0.5">{d.protein}<span className="text-zinc-300">g</span></p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-zinc-300">Carb</p><p className="text-white font-semibold mt-0.5">{d.carb}<span className="text-zinc-300">g</span></p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-zinc-300">Fat</p><p className="text-white font-semibold mt-0.5">{d.fat}<span className="text-zinc-300">g</span></p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-zinc-300">Fluid</p><p className="text-white font-semibold mt-0.5">{litresLabel(d.hydrationMl)}</p></div>
          </div>
        </div>

        {/* Fuelling breakdown — inline, only if there are sessions */}
        {fuel && fuel.sessions?.length ? (
          <div className="border-t border-white/10 mt-4 pt-4">
            <FuellingDay day={fuel} hydrationMl={null} unitSystem={unitSystem} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Step5Plan({ plan, onGenerate, regenerating, setPlan, isLite }) {
  const { generated, unitSystem, trainingWeek, profile, selections, mealPlanning } = plan;
  const guidelinesOnly = mealPlanning === false;
  const loading = !!regenerating;
  const targets = generated?.targets;
  const fuelBy = {};
  (generated?.fuelling || []).forEach((f) => { fuelBy[f.index] = f; });
  const sessionsByDay = {};
  (trainingWeek?.days || []).forEach((day, i) => { sessionsByDay[i] = day.sessions || []; });
  // Highlight today's card if the week matches the current calendar week.
  // Days are Mon=0 … Sun=6; JS getDay() returns Sun=0 so shift.
  const todayIndex = (new Date().getDay() + 6) % 7;

  // Backfill micros for older plans that were generated before Weekly Micros
  // shipped — so this Step 5 preview and the Step 8 section both light up
  // without forcing the athlete to regenerate. Also refetch when the cached
  // payload predates the "Top-ups needed" fields (food/gap) so athletes on
  // pre-existing plans see the new nudges without hitting Regenerate.
  // Skip entirely in guidelines-only mode — no meal pathways means no micros.
  const microsIsStale = generated?.micros && (generated.micros.nudges?.length && !("food" in generated.micros.nudges[0]));
  const microsNeedsFetch = !generated?.micros || microsIsStale;
  useEffect(() => {
    if (guidelinesOnly) return;
    if (!generated?.dayPathways || !microsNeedsFetch || isLite || !setPlan) return;
    (async () => {
      try {
        const sel = {};
        Object.keys(selections || {}).forEach((k) => { sel[String(k)] = selections[k]; });
        const { data } = await api.post("/engine/micros", { dayPathways: generated.dayPathways, selections: sel, profile });
        if (data?.micros) {
          setPlan((p) => ({ ...p, generated: { ...(p.generated || {}), micros: data.micros } }));
        }
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Micros backfill failed", e?.message || e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generated?.dayPathways, microsNeedsFetch, isLite, guidelinesOnly]);

  return (
    <div>
      <SectionHeading eyebrow="Step 06" title="Your Week — Train &amp; Fuel"
        desc="Each day shows today's training on top and the nutrition target directly beneath it. Colour marks priority — nothing more." />

      <StepInstructions>
        Hit <span className="text-[#E2E30A]">Generate Plan</span> to build your week. Each day is scannable in seconds: <b>what you're doing</b> up top, <b>how to fuel it</b> below.
      </StepInstructions>

      {guidelinesOnly && (
        <div data-testid="step5-guidelines-only" className="mb-6 border border-[#4C9AFF]/40 bg-[#4C9AFF]/[0.06] px-4 py-3 flex items-start gap-2.5">
          <Sparkles className="h-4 w-4 text-[#4C9AFF] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4C9AFF]">Guidelines only</p>
            <p className="text-[11px] text-zinc-100 mt-1 leading-relaxed">
              Meal planning is off — so we're giving you daily calorie, macro and hydration guidelines that scale with each session. No meal pathways, no shopping list, no vitamin rollup. Change your mind? Flip Meal Planning back on in Step 2.
            </p>
          </div>
        </div>
      )}

      <LowEnergyWarning targets={targets} profile={plan.profile} />

      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          {targets && (
            <div className="flex items-center gap-4 text-sm flex-wrap font-mono tabular-nums">
              <span className="text-zinc-300 uppercase text-xs tracking-wider font-sans">Week avg</span>
              <span className="text-white">{targets.weekAverage.energy} kcal</span>
              <span className="text-zinc-300">{targets.weekAverage.protein}<span className="opacity-70"> P</span></span>
              <span className="text-zinc-300">{targets.weekAverage.carb}<span className="opacity-70"> C</span></span>
              <span className="text-zinc-300">{targets.weekAverage.fat}<span className="opacity-70"> F</span></span>
            </div>
          )}
        </div>
        {/* Manual Regenerate button retained as an escape hatch — plans
            auto-regenerate on every input change (Feb 27 2026) so most
            athletes will never touch this. Hidden until a plan exists so
            the initial auto-build isn't confused with a manual action. */}
        {targets && (
          <Button data-testid="generate-plan-btn" onClick={onGenerate} disabled={loading}
            className="rounded-none bg-transparent border border-white/20 hover:border-[#E2E30A] hover:text-[#E2E30A] text-zinc-200 font-semibold uppercase tracking-wide transition-colors">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> Force Regenerate</>}
          </Button>
        )}
      </div>

      {!targets ? (
        <div className="border border-dashed border-white/15 p-12 text-center" data-testid="step5-autogen-hint">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
              <p className="text-zinc-200 font-semibold uppercase tracking-wider text-xs">Building your plan…</p>
              <p className="text-zinc-400 text-xs">Crunching training load, macros and hydration.</p>
            </div>
          ) : (
            <p className="text-zinc-300 text-sm">Fill in Profile + Training and your weekly plan will appear here automatically. No button needed.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="daily-targets-grid">
          {targets.daily.map((d) => (
            <DayCard key={d.index} d={d} sessions={sessionsByDay[d.index]} fuel={fuelBy[d.index]} unitSystem={unitSystem} isToday={d.index === todayIndex} />
          ))}
        </div>
      )}
    </div>
  );
}
